const STEPS = [
  {
    label: "01 CYBER RANGE",
    title: "Browser-based enterprise lab, always on",
    desc: "A full simulated company \u2014 domain controller, workstations, web apps, SIEM, EDR and an AI application to attack. No hardware, no setup, no VPN gymnastics. 200 labs across the track, open 24/7.",
  },
  {
    label: "02 RECORDED VIDEO",
    title: "180 recorded modules, yours to rewind",
    desc: "Short, tightly edited lessons recorded by practitioners \u2014 every command shown on screen. Watch at 2\u00D7 or three times over; the material does not move on until you do. Written walkthroughs accompany every lab.",
  },
  {
    label: "03 GRADED PROJECTS",
    title: "Ten artefacts for your portfolio",
    desc: "A packet analysis, a hardened lab build, a pentest report, an incident report, an ISMS risk register, three automations, an AI red team assessment, a multi-cloud hardening plan. You submit them online and get written feedback back \u2014 interviewers read work, not certificates.",
  },
  {
    label: "04 ASSESSMENT",
    title: "Clear the level to unlock the next",
    desc: "Each level ends in a timed, automatically graded lab exam mapped to its industry certification, taken whenever you are ready \u2014 so the exam you later sit externally is not a surprise.",
  },
];

const WHO_LIST = [
  "Students with no IT background \u2014 Level 1 starts at what a network is.",
  "IT and helpdesk professionals crossing into security \u2014 most place into Level 3 or 4 and skip what they do daily.",
  "Graduates chasing a first security job \u2014 Levels 1\u20136 plus the portfolio is the shortest honest route.",
  "Corporate teams upskilling \u2014 private online enrolments with team progress reporting.",
];

export default function HowItWorksSection() {
  return (
    <section id="labs" className="max-w-[1200px] mx-auto px-4 py-12 sm:px-6 md:px-10 sm:py-22">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-start">
        <div>
          <div
            className="font-mono text-[12px] text-[#71717a] mb-[18px]"
            style={{ letterSpacing: "0.18em" }}
          >
            07 — HOW IT WORKS
          </div>
          <h2
            className="font-bold text-white mb-[18px]"
            style={{
              fontSize: "clamp(30px, 3.7vw, 46px)",
              lineHeight: 1.06,
              letterSpacing: "-0.03em",
            }}
          >
            You learn online, in a lab, at your own pace
          </h2>
          <p className="text-[16px] sm:text-[18px] leading-[1.65] text-[#a1a1aa] mb-9">
            Everything is recorded video plus hands-on labs in the browser
            &mdash; no live classes, no offline centres, nothing to attend.
            Watching a video does not clear a level; clearing the lab range
            does.
          </p>
          <div className="grid gap-px bg-[#27272a] border border-[#27272a] rounded-xl overflow-hidden">
            {STEPS.map((step, i) => (
              <div key={i} className="bg-black p-6">
                <div
                  className="font-mono text-[11px] text-[#a3e635] mb-2"
                  style={{ letterSpacing: "0.12em" }}
                >
                  {step.label}
                </div>
                <div className="text-[16px] font-semibold mb-1.5">
                  {step.title}
                </div>
                <div className="text-[14px] text-[#a1a1aa] leading-relaxed">
                  {step.desc}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-5">
          <div className="border border-[#27272a] rounded-[14px] overflow-hidden bg-[#09090b]">
            <svg
              viewBox="0 0 600 380"
              className="w-full block bg-[#0b0b0d]"
            >
              {/* Terminal window background */}
              <rect width="600" height="380" fill="#0b0b0d" rx="0" />

              {/* Title bar */}
              <rect width="600" height="36" fill="#141417" />
              <circle cx="18" cy="18" r="5" fill="#ef4444" />
              <circle cx="34" cy="18" r="5" fill="#facc15" />
              <circle cx="50" cy="18" r="5" fill="#a3e635" />
              <text
                x="300"
                y="22"
                textAnchor="middle"
                fill="#52525b"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="11"
                letterSpacing="0.5"
              >
                cyber-range — bash
              </text>

              {/* Terminal content */}
              <text
                x="24"
                y="68"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                $
              </text>
              <text
                x="38"
                y="68"
                fill="#d4d4d8"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                hd-scan --target 10.0.1.0/24
              </text>

              {/* Progress bar */}
              <text
                x="24"
                y="96"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                [■■■■■■■■■■] 100%
              </text>
              <text
                x="170"
                y="96"
                fill="#71717a"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                256 hosts scanned in 4.2s
              </text>

              {/* Results */}
              <text
                x="24"
                y="128"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                ✓
              </text>
              <text
                x="42"
                y="128"
                fill="#d4d4d8"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                3 open ports found on 10.0.1.14
              </text>

              <text
                x="24"
                y="152"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                ✓
              </text>
              <text
                x="42"
                y="152"
                fill="#d4d4d8"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                Vulnerability: CVE-2024-3094 (XZ Utils)
              </text>

              <text
                x="24"
                y="176"
                fill="#facc15"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                !
              </text>
              <text
                x="36"
                y="176"
                fill="#facc15"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                Severity: CRITICAL — CVSS 10.0
              </text>

              <text
                x="24"
                y="200"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                ✓
              </text>
              <text
                x="42"
                y="200"
                fill="#d4d4d8"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                Exploitable via remote code execution
              </text>

              {/* Second command */}
              <text
                x="24"
                y="236"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                $
              </text>
              <text
                x="38"
                y="236"
                fill="#d4d4d8"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                hd-report --format pdf --output lab-03.pdf
              </text>

              <text
                x="24"
                y="264"
                fill="#71717a"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                Generating report...
              </text>

              <text
                x="24"
                y="292"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                ✓
              </text>
              <text
                x="42"
                y="292"
                fill="#d4d4d8"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="12"
              >
                Report generated: lab-03.pdf (12 pages)
              </text>

              {/* Graded badge */}
              <rect
                x="24"
                y="316"
                width="90"
                height="24"
                rx="4"
                fill="#a3e635"
                fillOpacity="0.15"
              />
              <text
                x="69"
                y="332"
                textAnchor="middle"
                fill="#a3e635"
                fontFamily="'JetBrains Mono', monospace"
                fontSize="10"
                letterSpacing="0.5"
              >
                GRADED ✓
              </text>

              {/* Border */}
              <rect
                x="0.5"
                y="0.5"
                width="599"
                height="379"
                fill="none"
                stroke="#27272a"
                rx="0"
              />
            </svg>
          </div>

          <div className="border border-[#27272a] rounded-[14px] p-5 sm:p-7 bg-[#09090b]">
            <div
              className="font-mono text-[11px] text-[#71717a] mb-[18px]"
              style={{ letterSpacing: "0.14em" }}
            >
              WHO THIS TRACK IS BUILT FOR
            </div>
            <div className="grid gap-3.5">
              {WHO_LIST.map((item, i) => (
                <div
                  key={i}
                  className="flex gap-3 text-[15px] text-[#d4d4d8] leading-snug"
                >
                  <span className="text-[#a3e635] font-mono">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {item}
                </div>
              ))}
            </div>
            <a
              href="#enroll"
              className="inline-block mt-6 border border-[#3f3f46] px-[22px] py-3 rounded-[7px] text-[14px] font-semibold hover:border-white transition-colors"
            >
              Take the placement test
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
